import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { toast } from 'react-toastify'

import ROUTES from 'src/constants/routes'
import { useAuth } from 'src/contexts/auth'

import { useStyles } from './styles'
import { phoneMask } from 'src/utils'

type FormData = {
  nome: string
  sobrenome: string
  email: string
  documento: string
  telefone: string
  fazenda: string
  senha: string
}

import { cpf } from 'cpf-cnpj-validator'
import { useCheckEmail } from 'src/hooks/api/user/mutations/useCheckUsername'
import { Button, Loading } from 'src/components'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { VALIDATIONS } from 'src/@types/validations'

export const Register: React.FC = () => {
  const navigate = useNavigate()
  const style = useStyles()

  const [showPassword, setShowPassword] = useState(false)

  const { mutateAsync: verifyEmail, isPending } = useCheckEmail()
  const { register: registerUser } = useAuth()

  const {
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: yupResolver(VALIDATIONS.User.registerSchema),
    defaultValues: {
      nome: '',
      sobrenome: '',
      email: '',
      documento: '',
      telefone: '',
      fazenda: '',
      senha: ''
    }
  })

  const email = watch('email')
  const documento = watch('documento')
  const telefone = watch('telefone')

  useEffect(() => {
    if (!email) return

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

    if (!isValidEmail) {
      clearErrors('email')
      return
    }

    const timeout = setTimeout(async () => {
      await checkUsername(email)
    }, 500)

    return () => clearTimeout(timeout)
  }, [email])

  async function checkUsername(email: string) {
    if (!email) return false

    try {
      await verifyEmail(email)
      clearErrors('email')
      return true
    } catch (e: any) {
      if (e.response?.data?.errorMessage === 'Email já cadastrado.') {
        setError(
          'email',
          {
            type: 'manual',
            message: 'E-mail indisponível'
          },
          {
            shouldFocus: false
          }
        )
        return false
      }

      toast.error('Erro ao verificar email.')
      return false
    }
  }

  const onSubmit = async (data: FormData) => {
    try {
      const isValid = await checkUsername(data.email)

      if (!isValid) return

      await registerUser(data)
    } catch {
      toast.error('Erro ao cadastrar')
    }
  }

  return (
    <div className={style.registerContainer}>
      <div className={style.registerForm}>
        <div className={style.registerBox}>
          <h1 className='register__title'>CONGADO</h1>
          <h2>Comece grátis</h2>
          <h1 className='register__sub-title'>Crie uma nova conta</h1>

          <div>
            <h2>Já tem uma conta?</h2>
            <p onClick={() => navigate(ROUTES.AUTH.LOGIN)} className={style.registerLogin}>
              Entrar
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={`${style.registerInputName} ${errors.nome ? style.inputWithError : ''}`}>
            <div>
              <label htmlFor='name'>Nome</label>
              <input id='name' {...register('nome')} placeholder='Digite seu nome' />
              {errors.nome && <span className={style.registerError}>{errors.nome.message}</span>}
            </div>

            <div className={`${errors.sobrenome ? style.inputWithError : ''}`}>
              <label htmlFor='lastName'>Sobrenome</label>
              <input id='lastName' {...register('sobrenome')} placeholder='Digite seu sobrenome' />
              {errors.sobrenome && <span className={style.registerError}>{errors.sobrenome.message}</span>}
            </div>
          </div>

          <div className={`${style.registerInput} ${errors.email ? style.inputWithError : ''}`}>
            <label htmlFor='email'>E-mail</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
              <input
                id='email'
                {...register('email')}
                type='email'
                placeholder='exemplo@email.com'
                onKeyDown={(e) => {
                  if (e.key === ' ') e.preventDefault()
                }}
              />
              <span className={style.passwordEye} tabIndex={0}>
                {isPending && <Loading className={style.inputLoading} />}
              </span>
            </div>
            {errors.email && <span className={style.registerError}>{errors.email.message}</span>}
          </div>

          <div className={`${style.registerInput} ${errors.documento ? style.inputWithError : ''}`}>
            <label htmlFor='document'>CPF</label>
            <input
              id='document'
              {...register('documento')}
              value={documento || ''}
              onChange={(e) => {
                const formatted = cpf.format(e.target.value.replace(/\D/g, ''))
                setValue('documento', formatted)
              }}
              maxLength={14}
              placeholder='000.000.000-00'
            />
            {errors.documento && <span className={style.registerError}>{errors.documento.message}</span>}
          </div>

          <div className={`${style.registerInput} ${errors.telefone ? style.inputWithError : ''}`}>
            <label htmlFor='phone'>Telefone</label>
            <input
              id='phone'
              {...register('telefone')}
              value={telefone || ''}
              onChange={(e) => {
                setValue('telefone', phoneMask(e.target.value))
              }}
              maxLength={15}
              placeholder='(00) 00000-0000'
            />
            {errors.telefone && <span className={style.registerError}>{errors.telefone.message}</span>}
          </div>

          <div className={`${style.registerInput} ${errors.fazenda ? style.inputWithError : ''}`}>
            <label htmlFor='farm'>Nome da Fazenda</label>
            <input id='farm' {...register('fazenda')} placeholder='Digite o nome da fazenda' />
            {errors.fazenda && <span className={style.registerError}>{errors.fazenda.message}</span>}
          </div>

          <div className={`${style.registerInput} ${errors.senha ? style.inputWithError : ''}`}>
            <label htmlFor='password'>Senha</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                {...register('senha')}
                placeholder='Mínimo 8 caracteres'
                style={{ paddingRight: 36, flex: 1 }}
              />
              <span
                onClick={() => setShowPassword((prev) => !prev)}
                className={style.passwordEye}
                tabIndex={0}
                role='button'
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setShowPassword((prev) => !prev)
                }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </span>
            </div>
            {errors.senha && (
              <div style={{ marginTop: 4 }}>
                <span className={style.registerError}>{errors.senha.message}</span>
              </div>
            )}
          </div>

          <Button
            className={style.registerButtom}
            type='submit'
            disabled={isSubmitting || isPending}
            isLoading={isSubmitting}
          >
            Criar Conta
          </Button>
        </form>
      </div>
      <div className={style.registerImageSide}></div>
    </div>
  )
}
